import { Button, Stack, Typography } from "@mui/material";
import { AMBER_STATUS_METRIC_VALUE_TEXT, panelStyle } from "@/theme";
import { WellnessScoreStatus } from "../types";
import { formatDateToISO } from "../helpers";
import { useTranslation } from "react-i18next";

export interface MetricValueCardProps {
  metricName: string;
  metricValue: string;
  unit?: string;
  status?: WellnessScoreStatus;
  lastUpdated?: string;
  ctaText: string;
  onCtaClick?: () => void;
  children?: React.ReactNode;
}

export default function MetricValueCard(props: MetricValueCardProps) {
  const {
    metricName,
    metricValue,
    unit,
    status,
    lastUpdated,
    ctaText,
    onCtaClick,
    children,
  } = props;

  const { t } = useTranslation();

  const getStatusColor = (status: WellnessScoreStatus | undefined) => {
    switch (status) {
      case WellnessScoreStatus.Healthy:
        return "accent1.main";
      case WellnessScoreStatus.NeedsAttention:
        return "secondary.700";
      case WellnessScoreStatus.NeedsSupport:
        return "error.main";
      default:
        return "neutral.500";
    }
  };

  const getStatusLabel = (status: WellnessScoreStatus | undefined) => {
    switch (status) {
      case WellnessScoreStatus.Healthy:
        return (
          <Stack
            sx={{
              backgroundColor: "accent1.main",
              borderRadius: 1,
              alignSelf: "start",
            }}
            padding={1}
          >
            <Typography
              fontWeight={600}
              fontSize={12}
              variant="body2"
              color="primary.contrastText"
            >
              {t("WellnessStatus.healthy")}
            </Typography>
          </Stack>
        );
      case WellnessScoreStatus.NeedsAttention:
        return (
          <Stack
            sx={{
              backgroundColor: "secondary.700",
              borderRadius: 1,
              alignSelf: "start",
            }}
            padding={1}
          >
            <Typography
              fontWeight={600}
              fontSize={12}
              variant="body2"
              color="neutral.900"
            >
              {t("WellnessStatus.needsAttention")}
            </Typography>
          </Stack>
        );
      case WellnessScoreStatus.NeedsSupport:
        return (
          <Stack
            sx={{
              backgroundColor: "error.main",
              borderRadius: 1,
              alignSelf: "start",
            }}
            padding={1}
          >
            <Typography
              fontWeight={600}
              fontSize={12}
              variant="body2"
              color="primary.contrastText"
            >
              {t("WellnessStatus.needsSupport")}
            </Typography>
          </Stack>
        );
      default:
        return (
          <Stack
            sx={{
              backgroundColor: "neutral.500",
              borderRadius: 1,
              alignSelf: "start",
            }}
            padding={1}
          >
            <Typography
              fontWeight={600}
              fontSize={12}
              variant="body2"
              color="primary.contrastText"
            >
              {t("WellnessStatus.incomplete")}
            </Typography>
          </Stack>
        );
    }
  };

  return (
    <Stack sx={{ ...panelStyle }} width="100%">
      <Stack
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography
          fontWeight={600}
          sx={{
            fontFamily: "Manrope",
            fontWeight: 600,
            fontSize: 16,
            lineHeight: 1.25,
            letterSpacing: "-0.03em",
          }}
        >
          {metricName}
        </Typography>
        {lastUpdated && (
          <Typography
            variant="body2"
            sx={{
              color: "neutral.500",
              fontSize: 14,
            }}
          >
            {formatDateToISO(new Date(lastUpdated))}
          </Typography>
        )}
      </Stack>

      <Stack
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        mt={1}
      >
        <Stack flexDirection="row" alignItems="baseline" gap={unit ? 0.5 : 0}>
          <Typography
            sx={{
              fontFamily: "Manrope",
              fontWeight: 700,
              fontSize: 28,
              lineHeight: 1.2,
              color:
                status === WellnessScoreStatus.NeedsAttention
                  ? AMBER_STATUS_METRIC_VALUE_TEXT
                  : getStatusColor(status),
            }}
          >
            {metricValue}
          </Typography>
          {unit && (
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: 14,
                color: "neutral.700",
              }}
            >
              {unit}
            </Typography>
          )}
        </Stack>
        {getStatusLabel(status)}
      </Stack>

      {children && (
        <Stack mt={2} width="100%">
          {children}
        </Stack>
      )}

      <Stack mt={2} width="100%">
        <Button
          variant="outlined"
          color="primary"
          onClick={onCtaClick}
          fullWidth
          sx={{
            borderRadius: 2,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {ctaText}
        </Button>
      </Stack>
    </Stack>
  );
}
